import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'

import { noop } from 'lodash-es'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import {
  CostMessagingModalContent,
  CostMessagingModalCoverageInfo,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { ShieldTriageIcon } from '../../atoms/icons/ShieldTriageIcon'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Subhead } from '../../atoms/subhead/Subhead'
import { Tooltip, TOOLTIP_TRIGGER_ICON, TooltipMaxWidth } from '../../atoms/tooltip/Tooltip'
import { IS_WEB } from '../../constants'
import { useFloatingStyles } from '../../hooks/useFloatingStyles'
import { CareNavigatorCareOptionsFooter } from '../../molecules/careNavigatorCareOptionsFooter/CareNavigatorCareOptionsFooter'
import { CoverageMessagingLink } from '../../molecules/coverageMessagingLink/CoverageMessagingLink'
import { BodyTextSize, SubheadSize } from '../../styles/typeStyles'
import { CareOptionCard, CareOptionCardProps } from '../../templates/careOptionCard/CareOptionCard'
import { ColumnView, RowView } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface CareOptionsProps {
  careOptionsList: CareOptionCardProps[]
  mainCareOption?: CareOptionCardProps
  header?: string
  showPrivacy?: boolean
  careNavigatorFooterEntryPointModal?: React.ReactNode
  careNavigatorFooterHeaderText?: React.ReactNode
  onContactCareNavigatorPress?: () => void
  careNavigatorLinkTestId?: string
  coverageModalContents?: CostMessagingModalContent[]
  coverageModalTitleText?: React.ReactNode
  costMessagingModalCoverageInfo?: CostMessagingModalCoverageInfo
  isCostMessagingEnabled: boolean
  trackCoverageModalPress: (isOpen: boolean) => void
  isLoading?: boolean
  hideHeader?: boolean
  careOptionsTitleComponent?: React.ReactNode
  careOptionsCustomStyle?: { container?: CSSObject; cardContainer?: CSSObject }
  mainCareOptionCustomStyle?: { card?: CSSObject; body?: CSSObject; cta?: CSSObject; ctaContainer?: CSSObject }
  mainCareOptionConfig?: {
    isFloatingCard?: boolean
    headerTextSize?: SubheadSize
    headerTextBold?: boolean
    descriptionTextSize?: BodyTextSize
    coverageLinkOnBottom?: boolean
  }
  contentContainerCustomStyle?: CSSObject
  customerName?: string
  numSessions?: string
  numberOfSessionByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
}

const Content = styled.View<{ theme: ThemeType; customStyle?: CSSObject }>(({ theme, customStyle }) => ({
  maxWidth: theme.breakpoints.isMobileSized ? 'auto' : '750px',
  marginTop: theme.breakpoints.isMobileSized ? theme.spacing['24px'] : theme.spacing['32px'],
  paddingHorizontal: theme.spacing['16px'],
  marginHorizontal: 'auto',
  marginBottom: theme.spacing['64px'],
  width: '100%',
  ...customStyle,
}))

const HeaderContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignItems: 'center',
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['24px'],
}))

const PrivacyContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  marginBottom: theme.spacing['12px'],
  alignItems: 'center',
  marginHorizontal: theme.breakpoints.isMobileSized ? 'auto' : 0,
}))

const StyledLockedIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['8px'],
}))

const CareOptionsContainer = styled.View<{ theme: ThemeType; customStyle?: CSSObject }>(({ theme, customStyle }) => ({
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['32px'],
  flexDirection: theme.breakpoints.isMobileSized ? 'column' : 'row',
  flexWrap: 'wrap',
  marginRight: theme.breakpoints.isMobileSized ? theme.spacing['0px'] : '-16px',
  ...customStyle,
}))

const CardContainer = styled.View<{ theme: ThemeType; customStyle?: CSSObject }>(({ theme, customStyle }) => ({
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['12px'] : theme.spacing['16px'],
  width: theme.breakpoints.isMobileSized ? '100%' : '351px',
  marginRight: theme.breakpoints.isMobileSized ? theme.spacing['0px'] : theme.spacing['16px'],
  ...customStyle,
}))

const MainCareOptionCard = styled.View<{ theme: ThemeType; customStyle?: CSSObject }>(({ theme, customStyle }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  border: `1px solid ${theme.colors.borderDefault}`,
  borderRadius: theme.spacing['20px'],
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['12px'] : theme.spacing['16px'],
  width: '100%',
  padding: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : '44px 24px',
  flexDirection: theme.breakpoints.isMobileSized ? 'column' : 'row',
  ...customStyle,
}))

const MainCareOptionHeaderContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
}))

const MainCareOptionBodyContainer = styled(ColumnView)<{ theme: ThemeType; customStyle?: CSSObject }>(
  ({ theme, customStyle }) => ({
    marginLeft: theme.breakpoints.isMobileSized ? 0 : theme.spacing['32px'],
    flex: 'auto',
    ...(theme.breakpoints.isMobileSized ? { marginTop: theme.spacing['16px'] } : { marginLeft: theme.spacing['32px'] }),
    alignItems: theme.breakpoints.isMobileSized ? 'center' : 'flex-start',
    justifyContent: 'center',
    ...customStyle,
  }),
)

const MainCareOptionIllustrationContainer = styled.View({
  alignSelf: 'center',
})

const MainCareOptionCta = styled(PrimaryButton)<{ theme: ThemeType; customStyle?: CSSObject }>(
  ({ theme, customStyle }) => ({
    marginTop: theme.spacing['12px'],
    width: '100%',
    ...(!theme.breakpoints.isMobileSized && { maxWidth: '203px' }),
    ...customStyle,
  }),
)

const MainCareOptionCTAContainer = styled.View<{ customStyle?: CSSObject }>(({ customStyle }) => ({
  width: '100%',
  ...customStyle,
}))

const CoverageLinkContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['12px'],
}))

const BottomCoverageLinkContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['16px'],
  padding: `${theme.spacing['12px']} ${theme.spacing['24px']}`,
}))

const TooltipStyled = styled(Tooltip)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['8px'],
}))

const LoadingContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingTop: theme.spacing['48px'],
  paddingBottom: theme.spacing['64px'],
}))

export const CareOptions: FunctionComponent<CareOptionsProps> = ({
  careOptionsList,
  mainCareOption,
  header,
  showPrivacy = true,
  careNavigatorFooterEntryPointModal,
  careNavigatorFooterHeaderText,
  onContactCareNavigatorPress = noop,
  careNavigatorLinkTestId = 'CareOptions-ContactCareNavigatorLink',
  coverageModalTitleText,
  coverageModalContents = [],
  costMessagingModalCoverageInfo = undefined,
  isCostMessagingEnabled,
  trackCoverageModalPress,
  isLoading,
  hideHeader = false,
  careOptionsTitleComponent,
  careOptionsCustomStyle,
  mainCareOptionCustomStyle,
  mainCareOptionConfig = {
    isFloatingCard: false,
    headerTextSize: SubheadSize.MEDIUM,
    descriptionTextSize: BodyTextSize.DEFAULT,
    headerTextBold: false,
    coverageLinkOnBottom: false,
  },
  contentContainerCustomStyle,
  customerName,
  numSessions,
  numberOfSessionByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
}) => {
  const { colors, isAuroraTheme } = useTheme() as ThemeType
  const floatingStyles = useFloatingStyles({ simpleBoxShadow: true })

  const {
    isFloatingCard: mainCareOptionIsFloatingCard,
    headerTextSize: mainCareOptionHeaderTextSize,
    headerTextBold: mainCareOptionHeaderTextBold,
    descriptionTextSize: mainCareOptionDescriptionTextSize,
    coverageLinkOnBottom,
  } = mainCareOptionConfig

  return (
    <>
      <Content testID={tID('CareOptions')} customStyle={contentContainerCustomStyle}>
        {!hideHeader && (
          <HeaderContainer>
            <Subhead
              text={
                header || (
                  <FormattedMessage
                    defaultMessage='How can we help?'
                    description='Header text to ask user how we can help'
                  />
                )
              }
              size={SubheadSize.LARGE}
              textAlign='center'
            />
            {!coverageLinkOnBottom && isCostMessagingEnabled && (
              <CoverageLinkContainer>
                <CoverageMessagingLink
                  coverageModalTitleText={coverageModalTitleText}
                  coverageModalContents={coverageModalContents}
                  costMessagingModalCoverageInfo={costMessagingModalCoverageInfo}
                  isCostMessagingEnabled={isCostMessagingEnabled}
                  trackCoverageModalPress={trackCoverageModalPress}
                  customerName={customerName}
                  numSessions={numSessions}
                  numberOfSessionByProgram={numberOfSessionByProgram}
                  displayVisitsPerIssuePerYear={displayVisitsPerIssuePerYear}
                  displayVisitsPerIssuePerYearByProgram={displayVisitsPerIssuePerYearByProgram}
                />
              </CoverageLinkContainer>
            )}
          </HeaderContainer>
        )}

        {mainCareOption && (
          <MainCareOptionCard
            testID={tID(mainCareOption.testId)}
            style={mainCareOptionIsFloatingCard ? floatingStyles.floatingCard : undefined}
            customStyle={mainCareOptionCustomStyle?.card}
          >
            <MainCareOptionIllustrationContainer>{mainCareOption.optionIcon}</MainCareOptionIllustrationContainer>
            <MainCareOptionBodyContainer customStyle={mainCareOptionCustomStyle?.body}>
              {mainCareOption.optionHeader && (
                <MainCareOptionHeaderContainer>
                  <Subhead
                    text={mainCareOption.optionHeader}
                    size={mainCareOptionHeaderTextSize ?? SubheadSize.MEDIUM}
                    bold={mainCareOptionHeaderTextBold}
                  />
                </MainCareOptionHeaderContainer>
              )}
              <RowView>
                {mainCareOption.optionDescription && (
                  <BodyText
                    textAlign='center'
                    text={mainCareOption.optionDescription}
                    size={mainCareOptionDescriptionTextSize ?? BodyTextSize.DEFAULT}
                    color={colors.textSecondary}
                  />
                )}
                {mainCareOption.tooltipContent && (
                  <TooltipStyled
                    triggerConfig={{
                      icon: TOOLTIP_TRIGGER_ICON.INFO,
                      size: 16,
                      isFilled: false,
                    }}
                    content={mainCareOption.tooltipContent}
                    tooltipSize={TooltipMaxWidth.WIDE}
                    hoverEnabled={IS_WEB}
                  />
                )}
              </RowView>
              <MainCareOptionCTAContainer customStyle={mainCareOptionCustomStyle?.ctaContainer}>
                <MainCareOptionCta
                  text={mainCareOption.ctaText}
                  onPress={mainCareOption.onPress || noop}
                  testID={tID(`${mainCareOption.testId}-cta`)}
                  customStyle={mainCareOptionCustomStyle?.cta}
                />
              </MainCareOptionCTAContainer>
              {coverageLinkOnBottom && isCostMessagingEnabled && (
                <BottomCoverageLinkContainer>
                  <CoverageMessagingLink
                    coverageModalTitleText={coverageModalTitleText}
                    coverageModalContents={coverageModalContents}
                    costMessagingModalCoverageInfo={costMessagingModalCoverageInfo}
                    isCostMessagingEnabled={isCostMessagingEnabled}
                    trackCoverageModalPress={trackCoverageModalPress}
                    size={BodyTextSize.DEFAULT}
                    underline={false}
                    customerName={customerName}
                    numSessions={numSessions}
                    numberOfSessionByProgram={numberOfSessionByProgram}
                    displayVisitsPerIssuePerYear={displayVisitsPerIssuePerYear}
                    displayVisitsPerIssuePerYearByProgram={displayVisitsPerIssuePerYearByProgram}
                  />
                </BottomCoverageLinkContainer>
              )}
            </MainCareOptionBodyContainer>
          </MainCareOptionCard>
        )}

        {careOptionsList.length > 0 &&
          (isLoading ? (
            <LoadingContainer>
              <LoadingIndicator topPadding={0} />
            </LoadingContainer>
          ) : (
            <CareOptionsContainer customStyle={careOptionsCustomStyle?.container}>
              {careOptionsTitleComponent}
              {careOptionsList.map((item, index) => {
                return (
                  <CardContainer key={index} customStyle={careOptionsCustomStyle?.cardContainer}>
                    <CareOptionCard {...item} useBorderStyles />
                  </CardContainer>
                )
              })}
            </CareOptionsContainer>
          ))}
        <CareNavigatorCareOptionsFooter
          careNavigatorLinkTestId={careNavigatorLinkTestId}
          onContactCareNavigatorPress={onContactCareNavigatorPress}
          headerText={careNavigatorFooterHeaderText}
        />
        {showPrivacy && (
          <PrivacyContainer>
            <StyledLockedIconContainer>
              <ShieldTriageIcon />
            </StyledLockedIconContainer>
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage='Responses are confidential'
                  description='Description text explaining that responses are confidential'
                />
              }
              size={BodyTextSize.BADGE}
              textAlign='center'
              color={isAuroraTheme ? colors.textPrimary : colors.textSecondary}
              bold
            />
          </PrivacyContainer>
        )}
      </Content>
      {careNavigatorFooterEntryPointModal}
    </>
  )
}
